import { useContext, useMemo, useState } from 'react';
import './SortingSettings.scss';
import { useInitChuteSettings } from '../api/useInitChuteSettings';
import { Chute, Direction, DirectionId, useChuteSettingsStore } from '../model/useChuteSettingsStore';
import {
  Block,
  Button,
  CloseIcon,
  ConfirmModal,
  CustomScrollbarV2,
  InputText,
  LanguageTypes,
  Loader,
  SoundNotificationContext,
  Span,
  TrashIcon,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { useShallow } from 'zustand/react/shallow';

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
  pointerWithin,
  type CollisionDetection,
} from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { addBagCodes, removeAllDataFromChute, removeBagCodes } from '../api';
import { ChuteStatus } from '../model/sortingSettings';
import { useTranslation } from 'react-i18next';
import useSignalRConnection from '../model/useSignalRConnection';

function containsCI(h: string, n: string) {
  return h.toLowerCase().includes(n.trim().toLowerCase());
}

type LoadingAssignments = Map<number, Set<DirectionId>>;

export default function SortingSettings() {
  useInitChuteSettings();
  const { t, i18n } = useTranslation();
  useSignalRConnection();

  const {
    snapshot,
    currentAssignments,
    search,
    setSearch,
    isLoading,
    error,
    canEditChute,
    resetAllConfirmed,
    requestClearChute,
    requestRemove,
    pendingConfirm,
    closeConfirm,
    getStatus,
    assign,
    unassignSilent,
    assignSilent,
  } = useChuteSettingsStore(
    useShallow((s) => ({
      snapshot: s.snapshot,
      currentAssignments: s.currentAssignments,
      search: s.search,
      setSearch: s.setSearch,
      isLoading: s.isLoading,
      unassignSilent: s.unassignSilent,
      assignSilent: s.assignSilent,
      error: s.error,
      getStatus: s.getStatus,
      canEditChute: s.canEditChute,
      requestResetAll: s.requestResetAll,
      resetAllConfirmed: s.resetAllConfirmed,
      requestClearChute: s.requestClearChute,
      requestRemove: s.requestRemove,
      pendingConfirm: s.pendingConfirm,
      assign: s.assign,
      closeConfirm: s.closeConfirm,
    })),
  );

  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const { createNotification } = useNotificationsV2();
  const [loadingAssignments, setLoadingAssignments] = useState<LoadingAssignments>(() => new Map());

  const addLoading = (chuteId: number, directionId: DirectionId) => {
    setLoadingAssignments((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(chuteId) ?? []);
      set.add(directionId);
      next.set(chuteId, set);
      return next;
    });
  };

  const removeLoading = (chuteId: number, directionId: DirectionId) => {
    setLoadingAssignments((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(chuteId) ?? []);
      set.delete(directionId);
      if (set.size === 0) next.delete(chuteId);
      else next.set(chuteId, set);
      return next;
    });
  };

  const isChipLoading = (chuteId: number, directionId: DirectionId) =>
    Boolean(loadingAssignments.get(chuteId)?.has(directionId));

  const directionsFiltered: Direction[] = useMemo(() => {
    const dirs = snapshot?.directions ?? [];
    const q = search.trim();
    const filtered = q ? dirs.filter((d) => containsCI(d.name, q)) : dirs;
    return filtered.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [snapshot?.directions, search]);

  const chutesSorted: Chute[] = useMemo(() => {
    return (snapshot?.chutes ?? []).slice();
  }, [snapshot?.chutes]);

  const directionById = useMemo(() => {
    const map = new Map<DirectionId, Direction>();
    for (const d of snapshot?.directions ?? []) map.set(d.id, d);
    return map;
  }, [snapshot?.directions]);

  const [activeDirectionId, setActiveDirectionId] = useState<DirectionId | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  function handleDragStart(e: DragStartEvent) {
    if (e.active.data.current?.type === 'direction') {
      setActiveDirectionId(e.active.id as DirectionId);
    }
  }

  const chuteById = useMemo(() => {
    const map = new Map<number, Chute>();
    for (const c of snapshot?.chutes ?? []) map.set(c.id, c);
    return map;
  }, [snapshot?.chutes]);

  const { mutate: onAddBagCodes } = useMutation({
    mutationKey: ['addBag'],
    mutationFn: addBagCodes,
    onMutate: ({ chuteId, sortingDirectoryId }) => {
      addLoading(chuteId, sortingDirectoryId);
      assign(chuteId, sortingDirectoryId);
    },
    onSuccess: (_data, { chuteId, sortingDirectoryId }) => {
      removeLoading(chuteId, sortingDirectoryId);
      onSoundSuccess?.();
    },
    onError: (_err, { chuteId, sortingDirectoryId }) => {
      removeLoading(chuteId, sortingDirectoryId);
      onSoundError?.();

      createNotification({
        type: 'error',
        message: {
          title: t('sortingSettings.errors.failedToAssignTitle'),
          desc: t('sortingSettings.errors.failedToAssignDesc'),
        },
      });
    },
  });

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    setActiveDirectionId(null);

    if (!over) return;
    if (active.data.current?.type !== 'direction') return;
    if (over.data.current?.type !== 'chute') return;

    const directionId = active.id as DirectionId;
    const chuteId = over.data.current?.chuteId as number;

    if (!canEditChute(chuteId)) {
      createNotification({
        type: 'error',
        message: {
          title: t('sortingSettings.chute.readonly.title'),
          desc: t('sortingSettings.chute.readonly.desc'),
        },
      });
      onSoundError?.();
      return;
    }

    const already = (currentAssignments[chuteId] ?? []).includes(directionId);
    if (already) {
      const directionName = directionById.get(directionId)?.name ?? String(directionId);
      const chuteName = chuteById.get(chuteId)?.name ?? t('sortingSettings.chute.fallbackName', { chuteId });

      createNotification({
        type: 'error',
        message: {
          title: t('sortingSettings.errors.alreadyAssignedTitle'),
          desc: t('sortingSettings.errors.alreadyAssignedDesc', { directionName, chuteName }),
        },
      });
      onSoundError?.();
      return;
    }

    if (isChipLoading(chuteId, directionId)) return;
    onAddBagCodes({ chuteId, sortingDirectoryId: directionId });
  }

  const collisionDetection: CollisionDetection = (args) => {
    const pointer = pointerWithin(args);
    return pointer.length ? pointer : rectIntersection(args);
  };

  const { mutate: onRemoveBagCodes } = useMutation({
    mutationKey: ['removeBag'],
    mutationFn: removeBagCodes,

    onMutate: ({ chuteId, sortingDirectoryId }) => {
      addLoading(chuteId, sortingDirectoryId);
      unassignSilent(chuteId, sortingDirectoryId);
      closeConfirm();
    },

    onSuccess: (_data, { chuteId, sortingDirectoryId }) => {
      removeLoading(chuteId, sortingDirectoryId);
      onSoundSuccess?.();
    },

    onError: (_err, { chuteId, sortingDirectoryId }) => {
      removeLoading(chuteId, sortingDirectoryId);

      // rollback: возвращаем чип обратно
      assignSilent(chuteId, sortingDirectoryId);

      onSoundError?.();
      createNotification({
        type: 'error',
        message: {
          title: t('sortingSettings.errors.failedToRemoveTitle'),
          desc: t('sortingSettings.errors.failedToRemoveDesc'),
        },
      });
    },
  });

  const { mutate: onClearChute, isPending: isClearingChute } = useMutation({
    mutationKey: ['clearChute'],
    mutationFn: (chuteId: number) => removeAllDataFromChute(chuteId),

    onMutate: async (chuteId) => {
      const prevIds = currentAssignments[chuteId] ?? [];
      for (const id of prevIds) unassignSilent(chuteId, id);

      closeConfirm();
      return { chuteId, prevIds };
    },

    onSuccess: () => {
      onSoundSuccess?.();
      createNotification({
        type: 'success',
        message: {
          title: t('sortingSettings.success.chuteClearedTitle'),
          desc: t('sortingSettings.success.chuteClearedDesc'),
        },
      });
    },

    onError: (_err, _chuteId, ctx) => {
      // rollback
      if (ctx?.prevIds?.length) {
        for (const id of ctx.prevIds) assignSilent(ctx.chuteId, id);
      }

      onSoundError?.();
      createNotification({
        type: 'error',
        message: {
          title: t('sortingSettings.errors.failedToClearChuteTitle'),
          desc: t('sortingSettings.errors.failedToClearChuteDesc'),
        },
      });
    },
  });

  const confirm = useMemo(() => {
    if (!pendingConfirm) return null;

    if (pendingConfirm.kind === 'remove') {
      const directionName = directionById.get(pendingConfirm.directionId)?.name ?? String(pendingConfirm.directionId);

      return {
        title: t('sortingSettings.confirm.remove.title'),
        desc: (
          <span>
            {t('sortingSettings.confirm.remove.desc', {
              directionName,
              chuteId: pendingConfirm.chuteId,
            })}
          </span>
        ),
        onSubmit: () => {
          if (isChipLoading(pendingConfirm.chuteId, pendingConfirm.directionId)) return;

          onRemoveBagCodes({
            chuteId: pendingConfirm.chuteId,
            sortingDirectoryId: pendingConfirm.directionId,
          });
        },
      };
    }

    if (pendingConfirm.kind === 'clearChute') {
      return {
        title: t('sortingSettings.confirm.clearChute.title'),
        desc: (
          <span>
            {t('sortingSettings.confirm.clearChute.desc', {
              chuteId: pendingConfirm.chuteId,
            })}
          </span>
        ),
        onSubmit: () => onClearChute(pendingConfirm.chuteId),
      };
    }

    if (pendingConfirm.kind === 'resetAll') {
      return {
        title: t('sortingSettings.confirm.resetAll.title'),
        desc: <span>{t('sortingSettings.confirm.resetAll.desc')}</span>,
        onSubmit: resetAllConfirmed,
      };
    }

    return null;
  }, [pendingConfirm, directionById, resetAllConfirmed, t]); // t — чтобы корректно обновлялось при смене языка

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDirectionId(null)}
    >
      <Block className="chuteSettingsPage">
        <Block className="chuteSettingsToolbar">
          <Span as="h2" fs={18} fw={800}>
            {t('sortingSettings.title')}
          </Span>

          {/* <Block className="chuteSettingsToolbar__actions">
            <Button type="error" disabled={!snapshot || isLoading} onClick={requestResetAll}>
              {t('sortingSettings.toolbar.resetAll')}
            </Button>
          </Block> */}
        </Block>

        <Block className="chuteSettingsBody">
          {/* Left panel: Directions */}
          <Block className="chuteSettingsLeft">
            <Span fw={700}>{t('sortingSettings.directions.title')}</Span>

            <InputText
              value={search}
              id="search_chute"
              label={t('sortingSettings.directions.searchLabel')}
              onChange={(val) => setSearch(val)}
            />

            <Span fs={12} color="rgba(2,6,23,0.6)">
              {t('sortingSettings.directions.total', { count: snapshot?.directions?.length ?? 0 })}
            </Span>

            <CustomScrollbarV2 maxHeight="calc(100vh - 290px)">
              <Block className="directionList">
                {directionsFiltered.map((d) => (
                  <DirectionDraggable key={d.id} direction={d} />
                ))}

                {!isLoading && directionsFiltered.length === 0 && (
                  <Block className="emptyState">
                    <Span fs={12} color="rgba(2,6,23,0.6)">
                      {t('sortingSettings.directions.empty')}
                    </Span>
                  </Block>
                )}
              </Block>
            </CustomScrollbarV2>
          </Block>

          {/* Right panel: Chutes grid */}
          <Block className="chuteSettingsRight">
            {isLoading && (
              <Block className="centerState">
                <Loader />
              </Block>
            )}

            {!isLoading && error && (
              <Block className="centerState">
                <Span color="#b42318" fw={700}>
                  {t('sortingSettings.states.failedToLoadTitle')}
                </Span>
                <Span fs={12} color="rgba(2,6,23,0.6)">
                  {error}
                </Span>
              </Block>
            )}

            {!isLoading && !error && snapshot && (
              <CustomScrollbarV2 height="calc(100vh - 200px)">
                <Block className="chuteGrid">
                  {chutesSorted.map((chute) => {
                    const ids = currentAssignments[chute.id] ?? [];
                    const editable = canEditChute(chute.id);
                    const status = getStatus(chute.id);
                    const isEmpty = ids.length === 0;

                    const cardClass = [
                      'chuteCard',
                      editable ? 'chuteCard--editable' : 'chuteCard--readonly',
                      editable ? (isEmpty ? 'chuteCard--empty' : 'chuteCard--filled') : '',
                    ].join(' ');

                    return (
                      <Block key={chute.id} className={cardClass}>
                        <Block className="chuteCard__header">
                          <Span fw={800}>{chute.name}</Span>

                          <Block className="chuteCard__headerActions">
                            {!editable ? (
                              <Block className="readonlyBadge">
                                <Span fs={12} fw={800} color="#1E3EBC">
                                  {status === ChuteStatus.Normal
                                    ? t('sortingSettings.chute.readonly.badgeActive')
                                    : t('sortingSettings.chute.readonly.badgeBlocked')}
                                </Span>
                              </Block>
                            ) : (
                              <>
                                {!isEmpty && (
                                  <Button
                                    type="ghost"
                                    onClick={() => requestClearChute(chute.id)}
                                    disabled={ids.length === 0}
                                    icon={<TrashIcon size="xs" color="red" />}
                                  >
                                    {t('sortingSettings.chute.editable.clear')}
                                  </Button>
                                )}
                              </>
                            )}
                          </Block>
                        </Block>

                        <Block className="chuteCard__content">
                          <ChuteDroppable chuteId={chute.id} disabled={!editable} isEmpty={isEmpty}>
                            {!editable && status === ChuteStatus.Disabled && (
                              <Block className="readonlyNotice">
                                <Span fw={800} fs={12} color="#1E3EBC">
                                  {t('sortingSettings.chute.readonly.manufacturerTitle')}
                                </Span>
                                <Span fs={12} color="rgba(2,6,23,0.65)">
                                  {t('sortingSettings.chute.readonly.manufacturerDesc')}
                                </Span>
                              </Block>
                            )}

                            {editable && !isEmpty && (
                              <Block className="assignedMeta">
                                <Span fs={12} fw={700} color="rgba(2,6,23,0.65)">
                                  {t('sortingSettings.chute.editable.assignedTitle')}
                                </Span>
                                <Block className="assignedCount">
                                  <Span fs={12} fw={900} color="#1E3EBC">
                                    {ids.length}
                                  </Span>
                                </Block>
                              </Block>
                            )}

                            {editable && isEmpty ? (
                              <Block className="dropHint">
                                <Span fs={12} color="rgba(2,6,23,0.55)">
                                  {t('sortingSettings.states.dragHint')}
                                </Span>
                              </Block>
                            ) : null}

                            {!isEmpty && (
                              <Block className="assignedPanel">
                                <Block className="chips">
                                  {ids.map((directionId) => {
                                    const name = directionById.get(directionId)?.name ?? String(directionId);
                                    const loading = isChipLoading(chute.id, directionId);

                                    return (
                                      <Block key={directionId} className={`chip ${loading ? 'chip--loading' : ''}`}>
                                        {loading ? (
                                          <Block className="chipLoader">
                                            <Loader size="xs" />
                                          </Block>
                                        ) : (
                                          <>
                                            <Span fs={12} fw={700}>
                                              {name}
                                            </Span>

                                            {editable && (
                                              <CloseIcon
                                                onClick={() => requestRemove(chute.id, directionId)}
                                                size="xs"
                                                cursor="pointer"
                                              />
                                            )}
                                          </>
                                        )}
                                      </Block>
                                    );
                                  })}
                                </Block>

                                {editable && (
                                  <Span className="dropHintInline" fs={11} color="rgba(2,6,23,0.45)">
                                    {t('sortingSettings.states.dropHintInline')}
                                  </Span>
                                )}
                              </Block>
                            )}
                          </ChuteDroppable>
                        </Block>
                      </Block>
                    );
                  })}
                </Block>
              </CustomScrollbarV2>
            )}
          </Block>
        </Block>

        {createPortal(
          <DragOverlay>
            {activeDirectionId ? (
              <Block className="dragOverlayChip">
                <Span fs={12} fw={700}>
                  {directionById.get(activeDirectionId)?.name ?? String(activeDirectionId)}
                </Span>
              </Block>
            ) : null}
          </DragOverlay>,
          document.body,
        )}

        <ConfirmModal
          title={confirm?.title}
          desc={confirm?.desc}
          isOpen={confirm != null}
          onClose={closeConfirm}
          onSubmit={confirm?.onSubmit ?? (() => {})}
          isLoading={isClearingChute}
          disabled={isClearingChute || confirm == null}
          lang={i18n.language}
        />
      </Block>
    </DndContext>
  );
}

function DirectionDraggable({ direction }: { direction: Direction }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: direction.id,
    data: { type: 'direction', directionId: direction.id },
  });

  return (
    <Block
      ref={setNodeRef}
      className={`directionRow ${isDragging ? 'directionRow--dragging' : ''}`}
      sx={{
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.25 : 1,
        touchAction: 'none',
      }}
      {...listeners}
      {...attributes}
    >
      <Span>{direction.name}</Span>
    </Block>
  );
}

function ChuteDroppable({
  chuteId,
  disabled,
  isEmpty,
  className,
  children,
}: {
  chuteId: number;
  disabled: boolean;
  isEmpty: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `chute-${chuteId}`,
    disabled,
    data: { type: 'chute', chuteId },
  });

  return (
    <Block
      ref={setNodeRef}
      className={[
        className ?? '',
        'chuteDropZone',
        isEmpty ? 'chuteDropZone--empty' : 'chuteDropZone--filled',
        isOver ? 'chuteDropZone--over' : '',
        disabled ? 'chuteDropZone--disabled' : '',
      ].join(' ')}
      sx={{ flex: 1, minHeight: 76 }}
    >
      {children}
    </Block>
  );
}
